// UserAdmin.jsx - Admin section for managing users
import React, { useState, useEffect, useCallback } from "react";
import { API_BASE } from "../../config/api";
import { httpRequest } from "../../utils/httpClient";
import { authService } from "../../services/auth";
import logger from '../../utils/logger';

function UserAdmin({ user }) {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [message, setMessage] = useState("");
  const [messageType, setMessageType] = useState(""); // success or error

  const showMessage = useCallback((text, type) => {
    setMessage(text);
    setMessageType(type);
    setTimeout(() => {
      setMessage("");
      setMessageType("");
    }, 3000);
  }, []);

  const fetchUsers = useCallback(async () => {
    try {
      setLoading(true);
      const res = await httpRequest(`${API_BASE}/admin/users`, {
        method: "GET",
        headers: authService.getAuthHeaders(),
      });

      if (res.ok) {
        const data = await res.json();
        setUsers(data.users || []);
      } else {
        throw new Error("Failed to fetch users");
      }
    } catch (err) {
      logger.error("Error fetching users:", err);
      showMessage("Failed to fetch users", "error");
    } finally {
      setLoading(false);
    }
  }, [showMessage]);

  // Fetch all users
  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  const toggleAdminStatus = async (userId, currentAdminStatus, userEmail) => {
    try {
      const newAdminStatus = !currentAdminStatus;
      const res = await httpRequest(`${API_BASE}/admin/users/${userId}/admin`, {
        method: "PUT",
        headers: {
          ...authService.getAuthHeaders(),
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ is_admin: newAdminStatus }),
      });

      if (res.ok) {
        // Update local state
        setUsers(prev =>
          prev.map(u =>
            u.id === userId ? { ...u, is_admin: newAdminStatus } : u
          )
        );
        showMessage(
          `${userEmail} ${newAdminStatus ? 'promoted to' : 'removed from'} admin`,
          "success"
        );
      } else {
        throw new Error("Failed to update admin status");
      }
    } catch (err) {
      logger.error("Error updating admin status:", err);
      showMessage("Failed to update admin status", "error");
    }
  };

  const filteredUsers = users.filter(user =>
    user.email?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    user.username?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  if (loading) {
    return <div className="admin-section">Loading users...</div>;
  }

  return (
    <div className="admin-section">
      <h2>User Management</h2>

      {message && (
        <div className={`admin-message ${messageType}`}>
          {message}
        </div>
      )}

      <div className="admin-controls">
        <input
          type="text"
          placeholder="Search users by email or username..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="admin-search-input"
        />
      </div>

      <div className="admin-table-container">
        <table className="admin-table">
          <thead>
            <tr>
              <th>ID</th>
              <th>Email</th>
              <th>Username</th>
              <th>BGG ID</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {filteredUsers.map((userItem) => (
              <tr key={userItem.id}>
                <td>{userItem.id}</td>
                <td>{userItem.email || 'N/A'}</td>
                <td>{userItem.username || 'N/A'}</td>
                <td>{userItem.bgg_id?.trim() || 'N/A'}</td>
                <td>
                  <button
                    onClick={() => toggleAdminStatus(userItem.id, userItem.is_admin, userItem.email)}
                    className={`admin-toggle-btn ${userItem.is_admin ? 'remove-admin' : 'make-admin'}`}
                    disabled={userItem.id === user.id} // Can't change own admin status
                  >
                    {userItem.id === user.id
                      ? 'Cannot change own status'
                      : userItem.is_admin
                        ? 'Remove Admin'
                        : 'Make Admin'
                    }
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>

        {filteredUsers.length === 0 && (
          <div className="admin-empty-state">
            {searchQuery ? 'No users found matching your search.' : 'No users found.'}
          </div>
        )}
      </div>
    </div>
  );
}

export default UserAdmin;
